// Fetches the real listed-expiry date chain for a token (e.g.
// ['2026-07-08', '2026-07-10', ..., '2027-06-25']) via the one-shot
// `latest_vol_surface` request (type: 'VOLSURFACE') — NOT the
// constant-maturity tenor codes ('1D'/'1W'/'1M'...) already exposed on
// AvailableToken.tenors, which are generic buckets, not real exchange
// dates.
//
// Deliberately one-shot, not `subscribe.latest_vol_surface`: the id for a
// streaming subscription must contain a SUBSCRIPTIONS_KEY substring (e.g.
// 'snapshot_data') for handle-messages.ts to route the follow-up `update`
// message correctly (see reference_diletta_ws_id_routing.md) — the ack
// alone otherwise reaches a one-shot handler once and the real data never
// arrives. A plain one-shot request/response avoids that whole class of
// bug and matches list_vol_surface_tokens' existing working pattern in
// use-available-tokens.ts.
//
// Used to resolve/validate shorthand expiry entry (see expiry-shorthand.ts)
// against what's actually tradable for a given token.
import { useContext, useEffect, useState } from 'react';

import { WebSocketContext } from '../websocket-context/websocket-provider';

// Cache + in-flight de-dupe, keyed by token — separate from
// use-available-tokens' single module-scope cache since expiries differ
// per token.
const cache = new Map<string, string[]>();
const inflight = new Map<string, Promise<string[]>>();

const requestListedExpiries = (
  token: string,
  sendMessage: (msg: string) => void,
  subscribeToChannel: (id: string, handler: (resp: unknown) => void) => void,
  unsubscribeFromChannel?: (id: string) => void,
): Promise<string[]> => {
  const cached = cache.get(token);
  if (cached) return Promise.resolve(cached);
  const existing = inflight.get(token);
  if (existing) return existing;

  const promise = new Promise<string[]>((resolve, reject) => {
    // Deliberately does NOT contain any SUBSCRIPTIONS_KEY substring — this
    // is a one-shot request, not a streaming subscription, so it must
    // route via the plain messageHandlers.get(id) path, not the
    // subscription-confirmation path.
    const reqId = `EXPIRY_CHAIN_${token}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    let settled = false;
    const finish = (out: string[] | Error) => {
      if (settled) return;
      settled = true;
      if (unsubscribeFromChannel) {
        try {
          unsubscribeFromChannel(reqId);
        } catch {
          /* ignore */
        }
      }
      inflight.delete(token);
      if (out instanceof Error) reject(out);
      else {
        cache.set(token, out);
        resolve(out);
      }
    };

    subscribeToChannel(reqId, (raw: unknown) => {
      const resp = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : undefined;
      if (resp?.error) {
        finish(new Error(`latest_vol_surface error: ${JSON.stringify(resp.error)}`));
        return;
      }
      const result = resp?.result;
      const tenors =
        result && typeof result === 'object'
          ? (result as Record<string, unknown>).tenors
          : undefined;
      if (!tenors || typeof tenors !== 'object') {
        finish(new Error('latest_vol_surface: unexpected response shape'));
        return;
      }
      finish(Object.keys(tenors));
    });

    sendMessage(
      JSON.stringify({
        jsonrpc: '2.0',
        id: reqId,
        method: 'latest_vol_surface',
        params: { token, type: 'VOLSURFACE', format: 'json', model: 'SVI' },
      }),
    );

    setTimeout(() => {
      if (!settled) finish(new Error('latest_vol_surface timed out'));
    }, 15000);
  });

  inflight.set(token, promise);
  return promise;
};

export const useListedExpiries = (
  token: string,
): { expiries: string[]; isLoading: boolean; error: string | null } => {
  const { sendMessage, subscribeToChannel, unsubscribeFromChannel, isConnected } =
    useContext(WebSocketContext);
  const [expiries, setExpiries] = useState<string[]>(cache.get(token) ?? []);
  const [isLoading, setIsLoading] = useState<boolean>(!cache.has(token));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const cached = cache.get(token);
    if (cached) {
      setExpiries(cached);
      setIsLoading(false);
      return;
    }
    if (!token || !isConnected || !sendMessage || !subscribeToChannel) return;

    let cancelled = false;
    setIsLoading(true);
    setError(null);
    requestListedExpiries(token, sendMessage, subscribeToChannel, unsubscribeFromChannel)
      .then((list) => {
        if (cancelled) return;
        setExpiries(list);
        setIsLoading(false);
      })
      .catch((e: Error) => {
        if (cancelled) return;
        setError(e.message);
        setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token, isConnected, sendMessage, subscribeToChannel, unsubscribeFromChannel]);

  return { expiries, isLoading, error };
};
